import React, { useEffect, useState } from "react";
import { showAlert } from "../../alertService";
import { ACCENT, BTN_PRIMARIO_SM, BTN_SECUNDARIO_SM, InfoField, STATUS_OPTIONS, normalizeInstagramHandle } from "./shared";
import { camposCategorias, categoriasDe } from "../../utils/proveedores";
import {
  PencilSquareIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

// ---------- Info tab ----------

const empty = <span className="text-zinc-300">—</span>;

const InfoTab = ({
  proveedor,
  categorias,
  allSubcategorias,
  updateField,
  serviciosCount = 0,
  onVerServicios,
  soloLectura = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [nombre, setNombre] = useState(proveedor.nombre || "");
  const [razonSocial, setRazonSocial] = useState(proveedor.razonSocial || "");
  const [rfc, setRfc] = useState(proveedor.rfc || "");
  const [website, setWebsite] = useState(proveedor.website || "");
  const [instagram, setInstagram] = useState(proveedor.instagram || "");
  const [direccion, setDireccion] = useState(proveedor.direccion || "");

  useEffect(() => setNombre(proveedor.nombre || ""), [proveedor.nombre]);
  useEffect(() => setRazonSocial(proveedor.razonSocial || ""), [proveedor.razonSocial]);
  useEffect(() => setRfc(proveedor.rfc || ""), [proveedor.rfc]);
  useEffect(() => setWebsite(proveedor.website || ""), [proveedor.website]);
  useEffect(() => setInstagram(proveedor.instagram || ""), [proveedor.instagram]);
  useEffect(() => setDireccion(proveedor.direccion || ""), [proveedor.direccion]);

  const saveIfChanged = (field, value, current) => {
    const v = (value || "").trim();
    const c = (current || "").trim();
    if (v === c) return;
    if (field === "nombre" && !v) {
      showAlert("El nombre del proveedor no puede estar vacío", "error");
      setNombre(current || "");
      return;
    }
    updateField({ [field]: v || null }, { field, value: v });
  };

  const saveInstagramIfChanged = () => {
    const normalized = normalizeInstagramHandle(instagram);
    const current = (proveedor.instagram || "").trim();
    if (normalized === current) {
      if (instagram !== normalized) setInstagram(normalized);
      return;
    }
    setInstagram(normalized);
    updateField(
      { instagram: normalized || null },
      { field: "instagram", value: normalized }
    );
  };

  // ── Categorías (varias, sin principal) ───────────────────────────────
  // Las filas guardadas vienen de categoriasDe(); en edición se les suma una
  // fila vacía mientras el usuario la llena. Cada cambio se guarda al vuelo
  // con camposCategorias(), que mantiene el espejo categoria/subcategoria e
  // isPlanner consistentes.
  const categoriasGuardadas = categoriasDe(proveedor);
  const [filaNueva, setFilaNueva] = useState(null); // {categoria, subcategoria} | null
  // La ficha reutiliza la instancia al cambiar de proveedor: la fila a medio
  // llenar y el modo edición no deben viajar al siguiente.
  useEffect(() => {
    setFilaNueva(null);
    setIsEditing(false);
  }, [proveedor.id]);
  const nombreCategoria = (id) => categorias.find((c) => c.id === id)?.nombre || null;
  const nombreSubcategoria = (id) => allSubcategorias.find((s) => s.id === id)?.nombre || null;
  const subcategoriasDe = (categoriaId) =>
    allSubcategorias.filter((s) => s.parentId === categoriaId);

  const guardarCategorias = (lista) => {
    const campos = camposCategorias(lista);
    updateField(campos, {
      field: "categorias",
      value: campos.categorias.map((e) => nombreCategoria(e.categoria) || e.categoria).join(", "),
    });
  };
  const cambiarCategoria = (idx, categoriaId) => {
    if (!categoriaId) return;
    const lista = categoriasGuardadas.map((e, i) =>
      i === idx ? { categoria: categoriaId, subcategoria: null } : e
    );
    guardarCategorias(lista);
  };
  const cambiarSubcategoria = (idx, subcategoriaId) => {
    const lista = categoriasGuardadas.map((e, i) =>
      i === idx ? { ...e, subcategoria: subcategoriaId || null } : e
    );
    guardarCategorias(lista);
  };
  const quitarCategoria = (idx) => {
    if (categoriasGuardadas.length <= 1) return;
    guardarCategorias(categoriasGuardadas.filter((_, i) => i !== idx));
  };
  const confirmarFilaNueva = (categoriaId) => {
    if (!categoriaId) return;
    const subs = subcategoriasDe(categoriaId);
    if (subs.length > 0) {
      // Deja elegir la subcategoría antes de guardar.
      setFilaNueva({ categoria: categoriaId, subcategoria: "" });
      return;
    }
    guardarCategorias([...categoriasGuardadas, { categoria: categoriaId, subcategoria: null }]);
    setFilaNueva(null);
  };
  const confirmarSubcategoriaNueva = (subcategoriaId) => {
    guardarCategorias([
      ...categoriasGuardadas,
      { categoria: filaNueva.categoria, subcategoria: subcategoriaId || null },
    ]);
    setFilaNueva(null);
  };
  const categoriasUsadas = new Set(categoriasGuardadas.map((e) => e.categoria));

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";
  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === (proveedor.status || "activo"))?.label || "Activo";

  // Solo lectura (P-FRA-13): la ficha nunca entra en modo edición.
  if (!isEditing || soloLectura) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-5">
        {!soloLectura && (
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className={BTN_SECUNDARIO_SM}
          >
            <PencilSquareIcon className="h-3.5 w-3.5" />
            Editar
          </button>
        </div>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
          <div className="sm:col-span-2">
            <InfoField label="Nombre">
              {proveedor.nombre || empty}
            </InfoField>
          </div>
          <InfoField label={categoriasGuardadas.length > 1 ? "Categorías" : "Categoría"}>
            {categoriasGuardadas.length > 0 ? (
              <ul className="space-y-0.5">
                {categoriasGuardadas.map((e) => {
                  const sub = e.subcategoria ? nombreSubcategoria(e.subcategoria) : null;
                  return (
                    <li key={e.categoria}>
                      {nombreCategoria(e.categoria) || e.categoria}
                      {sub && <span className="text-zinc-400"> · {sub}</span>}
                    </li>
                  );
                })}
              </ul>
            ) : empty}
          </InfoField>
          <InfoField label="Estado">
            <span className="font-medium">{statusLabel}</span>
          </InfoField>
          <InfoField label="Servicios en el catálogo">
            {/* Solo lectura aquí: los servicios y sus costos se ven en el tab
                Servicios y costos, y se editan en la ficha de cada uno. */}
            <span className="inline-flex items-center gap-2">
              {serviciosCount > 0 ? (
                <span className="font-medium">
                  {serviciosCount} {serviciosCount === 1 ? "servicio" : "servicios"}
                </span>
              ) : (
                <span className="text-zinc-400">Sin servicios registrados</span>
              )}
              <button
                type="button"
                onClick={onVerServicios}
                className="text-xs font-semibold text-blue-600 hover:underline"
              >
                {serviciosCount > 0 ? "Ver servicios" : "Agregar"}
              </button>
            </span>
          </InfoField>
          <InfoField label="Sitio web">
            {proveedor.website ? (
              <a
                href={/^https?:\/\//i.test(proveedor.website) ? proveedor.website : `https://${proveedor.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {proveedor.website}
              </a>
            ) : empty}
          </InfoField>
          <InfoField label="Instagram">
            {proveedor.instagram ? (
              <a
                href={`https://instagram.com/${proveedor.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 hover:underline"
              >
                @{proveedor.instagram}
              </a>
            ) : empty}
          </InfoField>
          <InfoField label="Razón social">{proveedor.razonSocial || empty}</InfoField>
          <InfoField label="RFC">{proveedor.rfc || empty}</InfoField>
          <div className="sm:col-span-2">
            <InfoField label="Dirección">{proveedor.direccion || empty}</InfoField>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">
          Los cambios se guardan automáticamente al salir del campo.
        </span>
        <button
          type="button"
          onClick={() => { setIsEditing(false); setFilaNueva(null); }}
          className={BTN_PRIMARIO_SM}
          style={{ backgroundColor: ACCENT }}
        >
          Listo
        </button>
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre</label>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onBlur={() => saveIfChanged("nombre", nombre, proveedor.nombre)}
          className={inputClass}
        />
      </div>
      {/* Categorías: una fila por categoría guardada + una fila para agregar.
          No hay principal: todas pesan igual. */}
      <div className="space-y-3">
        {categoriasGuardadas.map((e, idx) => {
          const subs = subcategoriasDe(e.categoria);
          return (
            <div
              key={e.categoria}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 sm:gap-3 items-end"
            >
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  {idx === 0 ? "Categoría" : `Categoría ${idx + 1}`}
                </label>
                <select
                  value={e.categoria}
                  onChange={(ev) => cambiarCategoria(idx, ev.target.value)}
                  className={inputClass + " bg-white"}
                >
                  {!nombreCategoria(e.categoria) && (
                    <option value={e.categoria}>{e.categoria}</option>
                  )}
                  {categorias
                    .filter((c) => c.id === e.categoria || !categoriasUsadas.has(c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Subcategoría</label>
                <select
                  value={e.subcategoria || ""}
                  onChange={(ev) => cambiarSubcategoria(idx, ev.target.value)}
                  disabled={subs.length === 0 && !e.subcategoria}
                  className={inputClass + " bg-white disabled:opacity-50"}
                >
                  <option value="">—</option>
                  {/* Subcategoría guardada que ya no existe o cambió de
                      padre: se muestra para poder quitarla. */}
                  {e.subcategoria && !subs.some((s) => s.id === e.subcategoria) && (
                    <option value={e.subcategoria}>{e.subcategoria}</option>
                  )}
                  {subs.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1 sm:h-[38px]">
                {categoriasGuardadas.length > 1 && (
                  <button
                    type="button"
                    onClick={() => quitarCategoria(idx)}
                    title="Quitar categoría"
                    className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filaNueva || categoriasGuardadas.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 sm:gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                {categoriasGuardadas.length === 0 ? "Categoría" : "Nueva categoría"}
              </label>
              <select
                value={filaNueva?.categoria || ""}
                onChange={(ev) => confirmarFilaNueva(ev.target.value)}
                className={inputClass + " bg-white"}
              >
                <option value="">— Selecciona —</option>
                {categorias
                  .filter((c) => !categoriasUsadas.has(c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Subcategoría</label>
              <select
                value={filaNueva?.subcategoria || ""}
                onChange={(ev) => confirmarSubcategoriaNueva(ev.target.value)}
                disabled={!filaNueva?.categoria}
                className={inputClass + " bg-white disabled:opacity-50"}
              >
                <option value="">
                  {filaNueva?.categoria ? "Sin subcategoría" : "—"}
                </option>
                {(filaNueva ? subcategoriasDe(filaNueva.categoria) : []).map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1 sm:h-[38px]">
              {filaNueva && (
                <>
                  <button
                    type="button"
                    onClick={() => confirmarSubcategoriaNueva("")}
                    className="px-2 py-1 rounded-lg text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 whitespace-nowrap"
                  >
                    Guardar sin subcategoría
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilaNueva(null)}
                    title="Cancelar"
                    className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setFilaNueva({ categoria: "", subcategoria: "" })}
            disabled={categorias.every((c) => categoriasUsadas.has(c.id))}
            className="inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-40"
            style={{ color: ACCENT }}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Agregar otra categoría
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Estado</label>
          <select
            value={proveedor.status || "activo"}
            onChange={(e) =>
              updateField(
                { status: e.target.value },
                { field: "status", value: e.target.value }
              )
            }
            className={inputClass + " bg-white"}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Sitio web</label>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            onBlur={() => saveIfChanged("website", website, proveedor.website)}
            placeholder="https://"
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Instagram</label>
        <div className="flex items-center gap-2">
          <span className="shrink-0 rounded-lg bg-zinc-100 px-2.5 py-1.5 text-sm font-medium text-zinc-500">@</span>
          <input
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
            onBlur={saveInstagramIfChanged}
            placeholder="tu_handle"
            className={inputClass}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Razón social</label>
          <input
            value={razonSocial}
            onChange={(e) => setRazonSocial(e.target.value)}
            onBlur={() => saveIfChanged("razonSocial", razonSocial, proveedor.razonSocial)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">RFC</label>
          <input
            value={rfc}
            onChange={(e) => setRfc(e.target.value)}
            onBlur={() => saveIfChanged("rfc", rfc, proveedor.rfc)}
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1">Dirección</label>
        <input
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          onBlur={() => saveIfChanged("direccion", direccion, proveedor.direccion)}
          className={inputClass}
        />
      </div>
    </div>
  );
};

export default InfoTab;
